
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Ref, Prop } from "vue-property-decorator";
    import { VForm } from "@/vForm";
    import * as toastr from "toastr";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import Store from "@/store/store";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import NumericId from "@/vue/components/NumericId.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import DatePicker from "@/vue/components/DatePicker.vue";
    import { ISiteLookup } from "@/model/Site";
    import { ILookupItem } from "@/model/LookupItem";
    import { UserRole, WorkPermitFormType } from "@/model/Enums";
    import { IWorkPermitSaveResponse } from "@/model/IWorkPermitSaveResponse";
    import { IClientLookup } from "@/model/Client";

    // // composed of basic lookup + info from sites
    interface IClientLookupWithFlag {
        id: string;
        description: string;
        hasWorkPermitForms: boolean;
        deleted: Date;
    }

    @Component({ components: { ApiButton, NumericId, ConfirmDialogue, DatePicker } })
    export default class WorkPermitAddDialogue extends Vue {

        //
        // -- properties
        //

        @Ref("addWorkPermitForm") private readonly addWorkPermitForm!: VForm;

        @Prop({ required: true })
        clientList!: Array<IClientLookup>;

        get canFilterClient(): boolean { return this.$store.state.signedInUser?.canFilterClient === true; }
        get clientOptions(): Array<IClientLookupWithFlag> {
            const list = Utils.clientOptionsFilter(this.clientList);
            return list.map(c => ({
                id: String(c.id),
                description: c.description,
                hasWorkPermitForms: this.siteList.filter(s => s.clientID === c.id && s.hasWorkPermitForms).length > 0,
                deleted: c.deleted
            }));
        }

        @Prop({ required: true })
        siteList!: Array<ISiteLookup>;
        get canFilterSite(): boolean { return this.$store.state.signedInUser?.canFilterSite === true; }
        get siteOptions(): Array<ISiteLookup> { return Utils.siteOptionsFilter(this.siteList, this.selectedClientID); }

        showDialogue: boolean = false;

        selectedClientID: string = Utils.emptyGuidValue;
        selectedSiteID: string = Utils.emptyGuidValue;

        //
        // -- methods
        //

        async show(): Promise<void> {
            const user = Store.state.signedInUser;
            this.selectedClientID = Utils.emptyGuidValue;
            this.selectedSiteID = Utils.emptyGuidValue;

            if(!this.canFilterClient) {
                this.selectedClientID = user?.clientIDs[0] ?? Utils.dummyGuidValue;
            }

            if(!this.canFilterSite) {
                const site = this.siteList.find(s => s.id === user?.siteID);
                if(site?.hasWorkPermitForms === true) {
                    // we can't choose client or site - so no need to show the dialogue
                    this.selectedSiteID = site.id;
                    await this.addWorkPermit();
                }
                else {
                    // bail out
                    toastr.error(`Cannot add permit form for ${site?.description}`);
                }
                return; // <-- skip out now if we're not admin or whatever (not showing dialogue)
            }

            const selectableSiteOptions = this.siteOptions.filter(s => s.hasWorkPermitForms);

            if (user?.role === UserRole.Site && this.siteOptions.filter(s => s.id === user.siteID && s.hasWorkPermitForms && !Utils.isEmptyId(s.id)).length > 0) {
                this.selectedSiteID = user.siteID;
                await this.addWorkPermit();
            }
            else if (selectableSiteOptions.length === 1) {
                this.selectedSiteID = selectableSiteOptions[0].id;
                await this.addWorkPermit();
            }
            else if (selectableSiteOptions.length === 0) {
                // this shouldn't happen!
                toastr.error("There are no sites available that use work permits.", "Cannot add Work Permit");
            }
            else {
                this.showDialogue = true;
                this.addWorkPermitForm?.resetValidation();
            }
        }

        validateSite(value: string|null): string|boolean {
            return Utils.isEmptyId(value) ? "Choose a site" : true;
        }

        async addWorkPermit(): Promise<void> {
            // if form not showing, addWorkPermitForm will be null - so assume valid
            if (!(this.addWorkPermitForm?.validate() ?? true)) {
                toastr.warning("Please fix highlighted issues", "Cannot Save");
                return;
            }
            const response: IWorkPermitSaveResponse = await ApiClient.get(`/api/workPermit/addNew?siteID=${this.selectedSiteID}`, "work-permit-add");
            if(response.isSuccess) {
                toastr.success("Added");
                this.$emit("added", response.newID);
                this.showDialogue = false;
            }
            else {
                toastr.error("Failed");
            }
        }
    }
